import { useEffect, useRef, useState } from "react";
import { Loader2, Pause, Play } from "lucide-react";

// LDF: Eigener Player statt <audio controls> – Safari schneidet die Standard-Leiste bei geringer Höhe oben ab.
const fmt = (s: number) => {
  const v = Math.max(0, Math.floor(s || 0));
  return `${Math.floor(v / 60)}:${String(v % 60).padStart(2, "0")}`;
};

export const AudioPill = ({
  src,
  autoPlay,
  onError,
  className,
}: {
  /** Adresse oder Funktion, die sie beim ersten Abspielen liefert (z. B. signierter Link) */
  src: string | (() => Promise<string>);
  autoPlay?: boolean;
  onError?: () => void;
  className?: string;
}) => {
  const audio = useRef<HTMLAudioElement | null>(null);
  const [url, setUrl] = useState<string | null>(typeof src === "string" ? src : null);
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    if (typeof src === "string") setUrl(src);
  }, [src]);

  useEffect(() => {
    if (autoPlay) toggle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = async () => {
    let el = audio.current;
    if (!el) return;
    if (!el.paused) {
      el.pause();
      return;
    }
    if (!url && typeof src === "function") {
      setLoading(true);
      try {
        const resolved = await src();
        setUrl(resolved);
        el.src = resolved;
      } catch {
        setLoading(false);
        onError?.();
        return;
      }
    }
    el = audio.current!;
    el.play()
      .catch(() => {
        setPlaying(false);
        onError?.();
      })
      .finally(() => setLoading(false));
  };

  const seek = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = audio.current;
    if (!el || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    el.currentTime = ((e.clientX - rect.left) / rect.width) * duration;
  };

  const progress = duration ? Math.min(100, (time / duration) * 100) : 0;
  return (
    <div className={`flex min-w-0 items-center gap-2 rounded-full border border-border bg-muted/60 py-1 pl-1 pr-3 ${className ?? ""}`}>
      <audio
        ref={audio}
        src={url ?? undefined}
        preload="metadata"
        playsInline
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => {
          setPlaying(false);
          setTime(0);
        }}
        onTimeUpdate={(e) => setTime(e.currentTarget.currentTime)}
        onLoadedMetadata={(e) => setDuration(Number.isFinite(e.currentTarget.duration) ? e.currentTarget.duration : 0)}
        onError={() => {
          setPlaying(false);
          setLoading(false);
          if (url) onError?.();
        }}
        onWaiting={() => setLoading(true)}
        onPlaying={() => setLoading(false)}
        className="hidden"
      />
      <button
        type="button"
        onClick={toggle}
        aria-label={playing ? "Pause" : "Abspielen"}
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground cursor-pointer"
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : playing ? <Pause className="h-4 w-4 fill-current" /> : <Play className="ml-0.5 h-4 w-4 fill-current" />}
      </button>
      <div className="h-1.5 min-w-10 flex-1 cursor-pointer rounded-full bg-border" onClick={seek}>
        <div className="h-full rounded-full bg-primary" style={{ width: `${progress}%` }} />
      </div>
      <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{fmt(playing || time ? time : duration)}</span>
    </div>
  );
};
